/**
 * Sub-hook for PO Lines export and import template functionality (CR-1558).
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { purchasingAPI } from '../../../../lib/api';
import { useView } from '../../../../contexts/ViewContext';
import { POLinesFilters } from './types';

interface UsePOLinesExportParams {
  filters: POLinesFilters;
  activeTab: string;
}

export function usePOLinesExport({ filters, activeTab }: UsePOLinesExportParams) {
  const { getViewQueryParams } = useView();

  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  /**
   * Build query params that map the current filter/tab state to API filter keys.
   * Used by both export and import-template download.
   */
  const buildFilterParams = useCallback((forExport: boolean = false): Record<string, string> => {
    const queryParams: Record<string, string> = {};

    if (filters.poNumber) queryParams['po__po_number__icontains'] = filters.poNumber;
    if (filters.lineNumber) queryParams['line_number'] = filters.lineNumber;
    if (filters.supplier) queryParams['po__supplier'] = filters.supplier;
    else if (filters.supplierSearch) queryParams['po__supplier__name__icontains'] = filters.supplierSearch;
    if (filters.customer) queryParams['po__customer'] = filters.customer;
    if (filters.partNumber) queryParams['part_number__icontains'] = filters.partNumber;
    if (filters.description) queryParams['description__icontains'] = filters.description;

    // CR-3413: Unified status filter - route to correct API field
    if (filters.status) {
      if (filters.status === 'on_hold') {
        queryParams['on_hold'] = 'true';
      } else if (filters.status === 'closed') {
        queryParams['status'] = 'received';
      } else if (filters.status === 'changed') {
        queryParams['acknowledge_status__in'] = 'change_proposed_supplier,change_proposed_customer';
      } else if (filters.status === 'wip') {
        queryParams['acknowledge_status__in'] = 'wip,accepted';
        if (!forExport) queryParams['on_hold'] = 'false';
      } else {
        queryParams['acknowledge_status'] = filters.status;
      }
    }

    if (filters.on_hold === 'yes') queryParams['on_hold'] = 'true';
    else if (filters.on_hold === 'no') queryParams['on_hold'] = 'false';
    if (filters.buyerId) queryParams['buyer_id__icontains'] = filters.buyerId;
    if (filters.batchSerialNo) queryParams['batch_serial_no__icontains'] = filters.batchSerialNo;
    if (filters.otifOverridden === 'yes') queryParams['otif_overridden'] = 'true';
    else if (filters.otifOverridden === 'no') queryParams['otif_overridden'] = 'false';
    if (filters.supplierGroup) queryParams['po__supplier__group__name__icontains'] = filters.supplierGroup;
    if (filters.oddFrom) queryParams['original_delivery_date__gte'] = filters.oddFrom;
    if (filters.oddTo) queryParams['original_delivery_date__lte'] = filters.oddTo;
    if (filters.ppv === 'positive') queryParams['ppv__gt'] = '0';
    else if (filters.ppv === 'negative') queryParams['ppv__lt'] = '0';
    else if (filters.ppv === 'zero') queryParams['ppv'] = '0';
    if (filters.commodityGroup) queryParams['commodity_group'] = filters.commodityGroup;
    if (filters.commodityType) queryParams['commodity_type'] = filters.commodityType;

    // Tab-based filtering
    if (activeTab === 'open') queryParams['status__in'] = 'pending,partially_received';
    else if (activeTab === 'closed') queryParams['status'] = 'received';
    else if (activeTab === 'on_hold') queryParams['on_hold'] = 'true';
    else if (activeTab === 'needs_action') queryParams['needs_action'] = 'true';

    return queryParams;
  }, [filters, activeTab]);

  // Export handler (CR-1558)
  const handleExport = useCallback(async (format: 'xlsx' | 'csv' = 'xlsx') => {
    try {
      setExporting(true);

      const queryParams = buildFilterParams(true);
      queryParams['file_format'] = format;

      const response = await purchasingAPI.exportLines(queryParams);
      const blob = new Blob([response.data], {
        type: format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `po_lines_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export completed successfully');
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error('Failed to export PO lines');
    } finally {
      setExporting(false);
    }
  }, [buildFilterParams]);

  // Build query params for the import template download (mirrors current view + filters)
  const getTemplateExportParams = useCallback((): Record<string, string> => {
    const viewParams = getViewQueryParams();
    const queryParams: Record<string, string> = {};

    // View context (site / group / company)
    if (viewParams['site'])    queryParams['site']    = String(viewParams['site']);
    if (viewParams['group'])   queryParams['group']   = String(viewParams['group']);
    if (viewParams['company']) queryParams['company'] = String(viewParams['company']);

    // Merge filter params
    const filterParams = buildFilterParams();
    Object.assign(queryParams, filterParams);

    return queryParams;
  }, [buildFilterParams, getViewQueryParams]);

  return {
    exporting,
    showImportModal,
    setShowImportModal,
    handleExport,
    getTemplateExportParams,
  };
}
